/*
 * Studio v2 — rendering. Garment photos and print zones (DecorateZones, shared with the live
 * Studio), designs as images (lettering, the shopper's upload, AI art keyed off its chroma
 * background), the mockup a proposal card or the review screen shows, and the production files
 * the cart carries (_art: the location's design at 150 dpi over the whole print area; _mockup).
 * Print geometry (the print area inside a zone, the size cap, the PNG density tag) is in
 * studio-print.js.
 */
(function () {
  const FONTS = {
    graduate: { name: 'Varsity', family: 'Graduate', weight: 400, style: 'normal' },
    bebas: { name: 'Condensed', family: 'Bebas Neue', weight: 400, style: 'normal' },
    archivo: { name: 'Heavy', family: 'Archivo Black', weight: 400, style: 'normal' },
    playfair: { name: 'Editorial', family: 'Playfair Display', weight: 800, style: 'italic' },
    marker: { name: 'Marker', family: 'Permanent Marker', weight: 400, style: 'normal' },
    pacifico: { name: 'Script', family: 'Pacifico', weight: 400, style: 'normal' },
    mono: { name: 'Mono', family: 'Space Mono', weight: 700, style: 'normal' },
  };
  const INKS = { White: '#ffffff', Black: '#141414', Navy: '#00263a', Maroon: '#8e1537', Scarlet: '#c8102e', Gold: '#f1b434', Kelly: '#00843d', Royal: '#1d4f91', Orange: '#e35205', Silver: '#a2aaad' };
  const P = window.StudioPrint;
  const ART_DPI = P.ART_DPI;
  const fontsReady = Promise.all(Object.values(FONTS).map((f) => document.fonts.load(`${f.style} ${f.weight} 100px "${f.family}"`).catch(() => {})));

  const hexRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const rgbHex = (c) => '#' + c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
  const lum = (c) => { const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; }; return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); };
  const contrast = (a, b) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
  // The ink to use on a garment: the asked-for ink if it reads (WCAG ≥ 2.2, as the live Studio),
  // else white or black, whichever reads better.
  function inkFor(name, garmentRgb) {
    const want = INKS[name] ? name : null;
    if (want && (!garmentRgb || contrast(hexRgb(INKS[want]), garmentRgb) >= 2.2)) return want;
    if (!garmentRgb) return want || 'White';
    return contrast([255, 255, 255], garmentRgb) >= contrast([20, 20, 20], garmentRgb) ? 'White' : 'Black';
  }

  const imgCache = new Map();
  function loadImage(src) {
    if (!src) return Promise.resolve(null);
    if (imgCache.has(src)) return imgCache.get(src);
    const p = new Promise((res) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });
    imgCache.set(src, p);
    // A failed load is not kept: a phone that could not decode a photo once often can a moment later (#MV1544).
    p.then((im) => { if (!im && imgCache.get(src) === p) imgCache.delete(src); });
    return p;
  }

  // ---------------------------------------------------------------- garments + zones
  const garments = new Map();
  function garment(p, colorName) {
    const c = p.colors.find((x) => x.name === colorName) || p.colors[0];
    const key = `${p.handle}|${c && c.name}`;
    if (garments.has(key)) return garments.get(key);
    const g = (async () => {
      const Z = window.DecorateZones;
      const meta = (p.views || {})[c.name] || {};
      const [front, back] = await Promise.all([loadImage(c.image), loadImage(meta.back)]);
      if (!front) throw new Error(`photo of ${p.title} in ${c.name} did not load`);
      // A CMS back shot can have other pixel dimensions than the front photo; zones are computed in
      // the front's pixel space, so the back is redrawn at the front's size first.
      const fit = (img) => { const c = document.createElement('canvas'); c.width = front.naturalWidth; c.height = front.naturalHeight; const k = Math.min(c.width / img.naturalWidth, c.height / img.naturalHeight); c.getContext('2d').drawImage(img, (c.width - img.naturalWidth * k) / 2, (c.height - img.naturalHeight * k) / 2, img.naturalWidth * k, img.naturalHeight * k); return c; };
      const imgs = { front, back: back ? fit(back) : Z.backImage(front) };
      const zones = Z.compute(p, imgs);
      const rgb = Z.averageColor(front).split(',').map(Number);
      return { product: p, color: c.name, front, back: imgs.back, W: front.naturalWidth, H: front.naturalHeight, zones, rgb, hex: rgbHex(rgb) };
    })();
    garments.set(key, g);
    g.catch(() => garments.delete(key));
    return g;
  }
  // Location keys a product offers (no photo needed): used by the rules layer.
  function zoneKeys(p) {
    try { return window.DecorateZones.definitions(p, false).map((d) => d.key); } catch (_) { return ['FC']; }
  }
  function zoneLabel(p, key) {
    const cms = (p.cmsLocations || []).find((l) => l.code === key);
    if (cms && cms.name) return cms.name.replace(/\s*\(.*\)\s*$/, '');
    try { const d = window.DecorateZones.definitions(p, false).find((x) => x.key === key); if (d) return d.label; } catch (_) {}
    return key;
  }

  // ---------------------------------------------------------------- designs
  // Lettering → a transparent canvas, every line at one font size.
  function textCanvas(d, garmentRgb, size = 200) {
    if (d.layout === 'roster') return rosterCanvas(d, garmentRgb, size);
    if (d.layout === 'pieces') return pieceCanvas(d, garmentRgb, size);
    if (d.lettering) return letterCanvas(d, garmentRgb, size);
    const f = FONTS[d.font] || FONTS.graduate;
    // Long lines wrap at word breaks (~14 characters) so a sentence prints big, not as one thin strip.
    // The wrap widens until every word fits in 6 lines: a shopper's quote lost its last words at a
    // fixed 14 characters x 5 lines (2026-10-12).
    const src = String(d.text || '').split(/\n|\\n/).map((s) => s.trim()).filter(Boolean);
    const wrap = (n) => src.flatMap((l) => l.split(/\s+/).reduce((acc, w) => { const last = acc[acc.length - 1]; if (last != null && (last + ' ' + w).length <= n) acc[acc.length - 1] = last + ' ' + w; else acc.push(w); return acc; }, []));
    let n = 14, lines = wrap(n);
    while (lines.length > 6 && n < 60) lines = wrap(n += 2);
    const lh = f.family === 'Pacifico' ? 1.45 : 1.08;
    const c = document.createElement('canvas'), ctx = c.getContext('2d');
    ctx.font = `${f.style} ${f.weight} ${size}px "${f.family}"`;
    const w = Math.max(...lines.map((l) => ctx.measureText(l).width), 10);
    c.width = Math.ceil(w + size * 0.3); c.height = Math.ceil(lines.length * size * lh + size * 0.3);
    ctx.font = `${f.style} ${f.weight} ${size}px "${f.family}"`;
    ctx.fillStyle = INKS[inkFor(d.ink, garmentRgb)];
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    lines.forEach((l, i) => ctx.fillText(l, c.width / 2, size * 0.15 + (i + 0.5) * size * lh));
    return c;
  }
  // The lettering screen (2026-10-19 revamp Phase 3): the lines as typed (C.letterLines), line 1 full size, lines 2 and 3
  // at 60%; line 1 straight or arched (d.arc: it bends up through ARC radians, the varsity arch). Trimmed to the ink.
  const ARC = 1.05;
  const fontOf = (f, s) => `${f.style} ${f.weight} ${s}px "${f.family}"`;
  function letterCanvas(d, garmentRgb, size = 200) {
    const f = FONTS[d.font] || FONTS.graduate, lh = f.family === 'Pacifico' ? 1.45 : 1.12;
    const lines = window.StudioCore.letterLines(d.text);
    if (!lines.length) lines.push(' ');
    const sz = lines.map((_, i) => Math.round(i ? size * 0.6 : size));
    const m = document.createElement('canvas').getContext('2d');
    const widths = lines.map((l, i) => { m.font = fontOf(f, sz[i]); return m.measureText(l).width; });
    // The arch: the first line's letters sit on a circle whose top is the line's middle.
    let r = 0, sag = 0, w0 = widths[0];
    if (d.arc && lines[0].trim()) { r = widths[0] / ARC; sag = r * (1 - Math.cos(ARC / 2)); w0 = 2 * r * Math.sin(ARC / 2) + sz[0]; }
    const pad = size * 0.25, W = Math.ceil(Math.max(w0, ...widths.slice(1), 10) + 2 * pad);
    const H = Math.ceil(sz.reduce((a, s) => a + s * lh, 0) + sag + 2 * pad + (r ? sz[0] * 0.3 : 0));
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = INKS[inkFor(d.ink, garmentRgb)];
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    let y = pad;
    lines.forEach((l, i) => {
      ctx.font = fontOf(f, sz[i]);
      if (i === 0 && r) {
        const cy = y + (sz[0] * lh) / 2 + r;
        let at = -widths[0] / 2;
        for (const ch of l) {
          const cw = ctx.measureText(ch).width, a = (at + cw / 2) / r;
          ctx.save(); ctx.translate(W / 2 + r * Math.sin(a), cy - r * Math.cos(a)); ctx.rotate(a); ctx.fillText(ch, 0, 0); ctx.restore();
          at += cw;
        }
        y += sz[0] * lh + sag;
      } else { ctx.fillText(l, W / 2, y + (sz[i] * lh) / 2); y += sz[i] * lh; }
    });
    return trim(c);
  }
  // A roster on the back (Phase 3): the same list on every piece. An optional heading (d.title) over the names, in one
  // column up to 8 names, two up to 24, else three; a number, when given, stands in front of its name.
  function rosterCanvas(d, garmentRgb, size = 200) {
    const f = FONTS[d.font] || FONTS.graduate, lh = 1.25;
    const rows = window.StudioCore.parseRoster(d.text);
    const title = String(d.title || '').trim();
    const ns = Math.round(size * 0.55), ts = Math.round(size * 0.85);
    const cols = rows.length <= 8 ? 1 : rows.length <= 24 ? 2 : 3, per = Math.max(1, Math.ceil(rows.length / cols));
    const m = document.createElement('canvas').getContext('2d'); m.font = fontOf(f, ns);
    const numW = rows.some((x) => x.number) ? Math.max(...rows.map((x) => m.measureText(x.number).width)) + ns * 0.5 : 0;
    const nameW = Math.max(10, ...rows.map((x) => m.measureText(x.name).width));
    const colW = numW + nameW, gap = ns * 1.4;
    m.font = fontOf(f, ts);
    const tw = title ? m.measureText(title).width : 0;
    const pad = size * 0.25, bodyW = cols * colW + (cols - 1) * gap;
    const W = Math.ceil(Math.max(bodyW, tw) + 2 * pad), top = title ? ts * 1.5 : 0;
    const c = document.createElement('canvas'); c.width = W; c.height = Math.ceil(top + per * ns * lh + 2 * pad);
    const ctx = c.getContext('2d');
    ctx.fillStyle = INKS[inkFor(d.ink, garmentRgb)]; ctx.textBaseline = 'middle';
    if (title) { ctx.font = fontOf(f, ts); ctx.textAlign = 'center'; ctx.fillText(title, W / 2, pad + ts * 0.6); }
    ctx.font = fontOf(f, ns);
    const x0 = (W - bodyW) / 2;
    rows.forEach((x, i) => {
      const col = Math.floor(i / per), row = i % per, cx = x0 + col * (colW + gap), y = pad + top + (row + 0.5) * ns * lh;
      if (numW) { ctx.textAlign = 'right'; ctx.fillText(x.number, cx + numW - ns * 0.5, y); }
      ctx.textAlign = numW ? 'left' : 'center'; ctx.fillText(x.name, numW ? cx + numW : cx + colW / 2, y);
    });
    return trim(c);
  }
  // Per-piece name and number (Phase 3 item 3): the jersey back, the name over a big number, for piece d.show (default
  // the first). Each piece gets its own print file at checkout. A piece with only a name or only a number prints that.
  function pieceCanvas(d, garmentRgb, size = 200) {
    const f = FONTS[d.font] || FONTS.graduate;
    const all = (d.pieces && d.pieces.length ? d.pieces : [{ name: 'NAME', number: '00' }]).map((x) => ({ name: String(x.name || '').toUpperCase(), num: String(x.number || '') }));
    const pc = all[d.show || 0] || all[0];
    const ns = Math.round(size * 0.6), bs = Math.round(size * 2.2);
    // One box for every piece (the widest name and number of the order), not trimmed: every piece prints at the same
    // scale and place, as on a team's jerseys; the names and numbers are centred in it. A group link's pieces are made one
    // at a time, before the list is known (d.box 'group'): the box is fixed for a 10-letter name and two digits
    // (d.hasName / d.hasNumber say which the group has), and a longer name is drawn smaller to fit.
    const group = d.box === 'group', ref = group ? [{ name: 'WASHINGTON', num: '88' }] : all;
    const m = document.createElement('canvas').getContext('2d');
    m.font = fontOf(f, ns); const nw = Math.max(0, ...ref.map((x) => (x.name ? m.measureText(x.name).width : 0)));
    const pw = pc.name ? m.measureText(pc.name).width : 0;
    m.font = fontOf(f, bs); const bw = Math.max(0, ...ref.map((x) => (x.num ? m.measureText(x.num).width : 0)), group && pc.num ? m.measureText(pc.num).width : 0);
    const hasName = group ? d.hasName !== false : all.some((x) => x.name), hasNum = group ? d.hasNumber !== false : all.some((x) => x.num);
    const pad = size * 0.1, W = Math.ceil(Math.max(nw, bw, 10) + 2 * pad);
    const H = Math.ceil((hasName ? ns * 1.25 : 0) + (hasNum ? bs * 0.95 : 0) + 2 * pad);
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = INKS[inkFor(d.ink, garmentRgb)]; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    if (hasName && pc.name) { ctx.font = fontOf(f, pw > nw ? Math.floor((ns * nw) / pw) : ns); ctx.fillText(pc.name, W / 2, pad + ns * 0.62); }
    if (hasNum && pc.num) { ctx.font = fontOf(f, bs); ctx.fillText(pc.num, W / 2, pad + (hasName ? ns * 1.25 : 0) + bs * 0.5); }
    return c;
  }
  // AI art comes on a flat chroma key (the model cannot draw transparency): key it out, with a
  // soft edge and the key's colour spill removed.
  function keyOut(img, keyHex) {
    const asked = hexRgb(keyHex || '#00ff00');
    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const id = ctx.getImageData(0, 0, c.width, c.height), d = id.data;
    // The model does not always paint the exact key it was asked for (a dull magenta, 2026-10-11):
    // use the median colour of the four corners when it is near the asked key.
    const W = c.width, H = c.height, pts = [];
    for (const [cx, cy] of [[0, 0], [W - 8, 0], [0, H - 8], [W - 8, H - 8]]) for (let y = cy; y < cy + 8; y += 2) for (let x = cx; x < cx + 8; x += 2) { const i = (y * W + x) * 4; pts.push([d[i], d[i + 1], d[i + 2]]); }
    const med = [0, 1, 2].map((j) => pts.map((q) => q[j]).sort((a, b) => a - b)[pts.length >> 1]);
    const k = Math.hypot(med[0] - asked[0], med[1] - asked[1], med[2] - asked[2]) < 200 ? med : asked;
    const gi = k.indexOf(Math.max(...k)); // the key's dominant channel
    for (let i = 0; i < d.length; i += 4) {
      const dist = Math.hypot(d[i] - k[0], d[i + 1] - k[1], d[i + 2] - k[2]);
      if (dist < 90) { d[i + 3] = 0; continue; }
      if (dist >= 170) continue; // the design itself: colours untouched
      d[i + 3] = Math.round((d[i + 3] * (dist - 90)) / 80);
      // Despill only in this soft edge band, toward the key's own channels: a green key pulls G
      // down to max(R, B); a magenta key pulls R and B down to G. (Despilling the whole image
      // turned a gold dog green-yellow, 2026-10-11.)
      if (k[1] > k[0] && k[1] > k[2]) d[i + 1] = Math.min(d[i + 1], Math.max(d[i], d[i + 2]));
      else if (k[0] > k[1] && k[2] > k[1]) { d[i] = Math.min(d[i], d[i + 1] + 40); d[i + 2] = Math.min(d[i + 2], d[i + 1] + 40); }
      else d[i + gi] = Math.min(d[i + gi], Math.max(...[0, 1, 2].filter((j) => j !== gi).map((j) => d[i + j])));
    }
    ctx.putImageData(id, 0, 0);
    return trim(c);
  }
  // A flat border colour (a logo saved on a white box) becomes transparent, flood-filled from the
  // edges. Photos and art that bleeds to the edge are left alone. (Port of the live Studio's rule.)
  function removeBorder(src, max = 2048) {
    // Worked at up to 2048 px: what this returns is what prints, and 2048 px is a 20 in print at 100 ppi
    // (it was 1600, which capped every upload at 16 in). The logo-as-is path works at 3000 px: a 20 in print at 150 ppi.
    const W = src.naturalWidth || src.width, H = src.naturalHeight || src.height, f = Math.min(1, max / Math.max(W, H));
    const w = Math.max(1, Math.round(W * f)), h = Math.max(1, Math.round(H * f));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(src, 0, 0, w, h);
    const id = ctx.getImageData(0, 0, w, h), d = id.data, border = [];
    // Sample a ring 3 px in: after keying, the outermost pixels are a soft half-transparent edge,
    // which hid a flat box behind AI-edited art (2026-10-11).
    const m = Math.min(3, Math.floor(Math.min(w, h) / 4));
    for (let x = m; x < w - m; x += 2) border.push(m * w + x, (h - 1 - m) * w + x);
    for (let y = m; y < h - m; y += 2) border.push(y * w + m, y * w + w - 1 - m);
    const opaque = border.filter((i) => d[i * 4 + 3] > 200);
    if (opaque.length < border.length * 0.6) return trim(c);
    const med = [0, 1, 2].map((j) => { const v = opaque.map((i) => d[i * 4 + j]).sort((a, b) => a - b); return v[v.length >> 1]; });
    const near = (i, t) => Math.abs(d[i * 4] - med[0]) + Math.abs(d[i * 4 + 1] - med[1]) + Math.abs(d[i * 4 + 2] - med[2]) < t;
    if (opaque.filter((i) => near(i, 40)).length < opaque.length * 0.8) return trim(c);
    const seen = new Uint8Array(w * h), stack = border.filter((i) => near(i, 60));
    stack.forEach((i) => (seen[i] = 1));
    while (stack.length) {
      const i = stack.pop(), x = i % w, y = (i / w) | 0;
      d[i * 4 + 3] = 0;
      [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]].forEach(([a, b]) => {
        if (a < 0 || b < 0 || a >= w || b >= h) return;
        const j = b * w + a;
        if (!seen[j] && near(j, 60)) { seen[j] = 1; stack.push(j); }
      });
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if ((x < m || y < m || x >= w - m || y >= h - m) && near(y * w + x, 90)) d[(y * w + x) * 4 + 3] = 0;
    ctx.putImageData(id, 0, 0);
    return trim(c);
  }
  // "Clean up my logo" (2026-10-19 revamp Phase 2, proposal section 5): a clean copy of a logo photographed on paper,
  // saved on a coloured box or cut from a screenshot, made here with no AI, so the shapes, words and colours stay the
  // shopper's. The background colour (the median of the edge ring) goes everywhere it is, also inside letters (removeBorder
  // only clears what touches the edge), with a soft edge whose colour is un-mixed from the background; paper shading is
  // allowed for by how much the edge ring itself varies. Then only the logo is kept: the largest solid part and whatever
  // sits near it (a screenshot's status bar and buttons go). The shopper sees it beside the original and chooses.
  function cleanLogo(src, max = 3000) {
    const W = src.naturalWidth || src.width, H = src.naturalHeight || src.height, f = Math.min(1, max / Math.max(W, H));
    const w = Math.max(1, Math.round(W * f)), h = Math.max(1, Math.round(H * f));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(src, 0, 0, w, h);
    const id = ctx.getImageData(0, 0, w, h), d = id.data, ring = [];
    const m = Math.min(2, Math.floor(Math.min(w, h) / 4));
    for (let x = m; x < w - m; x += 2) ring.push(m * w + x, (h - 1 - m) * w + x);
    for (let y = m; y < h - m; y += 2) ring.push(y * w + m, y * w + w - 1 - m);
    const opaque = ring.filter((i) => d[i * 4 + 3] > 200);
    if (opaque.length >= ring.length * 0.5) {
      const bg = [0, 1, 2].map((j) => { const v = opaque.map((i) => d[i * 4 + j]).sort((a, b) => a - b); return v[v.length >> 1]; });
      const dist = (i) => Math.hypot(d[i] - bg[0], d[i + 1] - bg[1], d[i + 2] - bg[2]);
      const spread = opaque.map((i) => dist(i * 4)).sort((a, b) => a - b)[Math.floor(opaque.length * 0.9)] || 0;
      const t0 = Math.min(110, Math.max(36, spread * 1.6)), t1 = t0 + 44;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        const e = dist(i);
        if (e <= t0) { d[i + 3] = 0; continue; }
        if (e >= t1) continue;
        const a = (e - t0) / (t1 - t0);
        for (let j = 0; j < 3; j++) d[i + j] = Math.max(0, Math.min(255, Math.round((d[i + j] - bg[j] * (1 - a)) / a)));
        d[i + 3] = Math.round(d[i + 3] * a);
      }
    }
    // Keep the logo: label solid parts on a coarse grid, keep the largest and every part within 12% of its size of it.
    const s = Math.max(1, Math.round(Math.max(w, h) / 600)), gw = Math.ceil(w / s), gh = Math.ceil(h / s), grid = new Int32Array(gw * gh);
    for (let y = 0; y < h; y += s) for (let x = 0; x < w; x += s) if (d[(y * w + x) * 4 + 3] > 128) grid[((y / s) | 0) * gw + ((x / s) | 0)] = -1;
    const parts = [];
    for (let i = 0; i < grid.length; i++) {
      if (grid[i] !== -1) continue;
      const id2 = parts.length + 1, st = [i], b = { n: 0, x0: gw, y0: gh, x1: 0, y1: 0, id: id2 };
      grid[i] = id2;
      while (st.length) {
        const k = st.pop(), x = k % gw, y = (k / gw) | 0;
        b.n++; if (x < b.x0) b.x0 = x; if (y < b.y0) b.y0 = y; if (x > b.x1) b.x1 = x; if (y > b.y1) b.y1 = y;
        for (const [a2, b2] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) { if (a2 < 0 || b2 < 0 || a2 >= gw || b2 >= gh) continue; const j = b2 * gw + a2; if (grid[j] === -1) { grid[j] = id2; st.push(j); } }
      }
      parts.push(b);
    }
    if (parts.length > 1) {
      const big = parts.reduce((a, b) => (b.n > a.n ? b : a));
      const pad = 0.12 * Math.max(big.x1 - big.x0, big.y1 - big.y0);
      // Grow the kept box with every part near it, until nothing more joins.
      const keep = new Set([big.id]); let box = { x0: big.x0 - pad, y0: big.y0 - pad, x1: big.x1 + pad, y1: big.y1 + pad }, grew = true;
      while (grew) {
        grew = false;
        for (const q of parts) if (!keep.has(q.id) && q.x1 >= box.x0 && q.x0 <= box.x1 && q.y1 >= box.y0 && q.y0 <= box.y1) { keep.add(q.id); box = { x0: Math.min(box.x0, q.x0 - pad), y0: Math.min(box.y0, q.y0 - pad), x1: Math.max(box.x1, q.x1 + pad), y1: Math.max(box.y1, q.y1 + pad) }; grew = true; }
      }
      // Clear everything outside the kept parts' box (soft edge pixels near the logo stay).
      const kb = [...keep].map((k) => parts[k - 1]).reduce((a, q) => ({ x0: Math.min(a.x0, q.x0), y0: Math.min(a.y0, q.y0), x1: Math.max(a.x1, q.x1), y1: Math.max(a.y1, q.y1) }), { x0: gw, y0: gh, x1: 0, y1: 0 });
      const X0 = (kb.x0 - 1) * s, Y0 = (kb.y0 - 1) * s, X1 = (kb.x1 + 2) * s, Y1 = (kb.y1 + 2) * s;
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (x < X0 || x > X1 || y < Y0 || y > Y1) d[(y * w + x) * 4 + 3] = 0;
    }
    ctx.putImageData(id, 0, 0);
    return trim(c);
  }
  // Crop to the opaque pixels.
  function trim(c) {
    const ctx = c.getContext('2d', { willReadFrequently: true }), { width: w, height: h } = c;
    const d = ctx.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 16) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    if (x1 < 0) return c;
    const out = document.createElement('canvas');
    out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  // Design → drawable (canvas or image), cached per design object and garment colour.
  const designCache = new Map(); // `${kind}|${src}` → Promise<canvas> (designs are copied in snapshots, so key by source)
  async function drawable(design, garmentRgb) {
    await fontsReady;
    if (design.kind === 'text') return textCanvas(design, garmentRgb);
    const k = design.kind, ck = `${k}|${design.keepBackground ? 1 : 0}|${design.asIs ? 1 : 0}|${design.src}`;
    if (designCache.has(ck)) return designCache.get(ck);
    const p = (async () => {
      const img = await loadImage(design.src);
      if (!img) throw new Error('design image did not load');
      // The image model sometimes paints a dark or white box instead of the key: after keying,
      // a flat border colour that is left is removed too.
      if (k === 'art') return removeBorder(keyOut(img, design.key || '#00ff00'));
      return design.keepBackground ? img : removeBorder(img, design.asIs ? 3000 : 2048);
    })();
    designCache.set(ck, p);
    p.catch(() => designCache.delete(ck));
    return p;
  }

  // Where a design sits in a zone: fitted to 92% of the zone's print area (its true shape, inside the
  // zone), centred. Uploads and AI art are rasters nobody can redraw larger, so they are capped at the width
  // their pixels print at 100 ppi (P.capped); lettering is redrawn at print size and is not capped. The mockup and the print file
  // both use this box, so what the shopper sees is what prints.
  // The logo-as-is path also carries the shopper's width (design.widthIn) and drag position (design.pos), and caps at
  // 150 ppi (design.asIs), what the POD pre-flight wants.
  function box(z, art, design) {
    const w = art.width || art.naturalWidth, h = art.height || art.naturalHeight, D = design || {};
    return P.place(z, w, h, { srcPx: P.capped(D.kind) ? w : null, widthIn: D.widthIn, pos: D.pos, minPpi: D.asIs ? P.ART_DPI : null });
  }
  // The garment, zone, design drawable and box of one decoration (the logo screen drags and sizes with these).
  async function placed(p, colorName, d) {
    const g = await garment(p, colorName);
    const z = g.zones.find((x) => x.key === d.location);
    if (!z) return null;
    const art = await drawable(d.design, g.rgb);
    return { g, z, art, b: box(z, art, d.design) };
  }

  // Where a decoration's print sits on the front lay-down: its box, plus the garment's opaque top and height
  // (the print's top edge as a share of the garment, for the model shot in experiment studio-model-shot).
  async function printBox(p, colorName, d) {
    const g = await garment(p, colorName);
    const z = g.zones.find((x) => x.key === d.location);
    if (!z) return null;
    const b = box(z, await drawable(d.design, g.rgb), d.design);
    const s = 200, c = document.createElement('canvas'); c.width = s; c.height = s;
    const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(z.view === 'back' ? g.back : g.front, 0, 0, s, s);
    const a = ctx.getImageData(0, 0, s, s).data;
    let top = -1, bottom = -1;
    for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) if (a[(y * s + x) * 4 + 3] > 20) { if (top < 0) top = y; bottom = y; break; }
    const k = g.H / s, kx = g.W / s;
    if (top < 0) return { b, top: 0, H: g.H, chest: null };
    // Body width (the opaque run through the centre column). Just below the armpits (45% down) it is the chest when
    // the sleeves stand clear of the body there (tees, tanks): then it matches the lower body. Hoodies and crews lie
    // with the sleeves against the body down to ~80%; there the body is read at 90% (above the cuffs), where it is
    // its own run. Measured 2026-10-15: 207 tee 80/82 px, 26424 tank 73/76; 139 hood 109 (with sleeves) vs 70.
    const run = (f) => {
      const y = Math.round(top + f * (bottom - top)), op = (x) => a[(y * s + x) * 4 + 3] > 20, c0 = Math.round(s / 2);
      if (!op(c0)) return null;
      let l = c0, r = c0, n = 0;
      while (l > 0 && op(l - 1)) l--;
      while (r < s - 1 && op(r + 1)) r++;
      for (let x = 0; x < s; x++) if (op(x)) n++;
      return { w: r - l + 1, alone: r - l + 1 < n };
    };
    const r45 = run(0.45), r90 = run(0.9);
    let chest = null;
    if (r45 && r90 && Math.abs(r45.w - r90.w) <= 0.15 * r90.w) chest = r45.w;
    else if (r90 && r90.alone) chest = r90.w;
    if (chest != null) chest *= kx;
    if (z.view !== 'front') chest = null;
    return { b, top: top * k, H: (bottom - top + 1) * k, chest };
  }

  // Mockup: the garment with every decoration printed on it. Front, plus the back when a
  // decoration is on the back. Returns a canvas `width` px wide (or the photo's own size).
  async function mockup(p, colorName, decorations, opts = {}) {
    const g = await garment(p, colorName);
    const decs = decorations || [];
    const views = opts.views || ['front'].concat(decs.some((d) => (g.zones.find((z) => z.key === d.location) || {}).view === 'back') ? ['back'] : []);
    const scale = opts.width ? opts.width / (g.W * views.length) : 1;
    const c = document.createElement('canvas');
    c.width = Math.round(g.W * views.length * scale); c.height = Math.round(g.H * scale);
    const ctx = c.getContext('2d');
    if (opts.background !== false) { ctx.fillStyle = opts.background || '#ffffff'; ctx.fillRect(0, 0, c.width, c.height); }
    ctx.scale(scale, scale);
    for (const [i, v] of views.entries()) {
      ctx.save();
      ctx.translate(i * g.W, 0);
      ctx.drawImage(v === 'back' ? g.back : g.front, 0, 0, g.W, g.H);
      for (const d of decs) {
        const z = g.zones.find((x) => x.key === d.location);
        if (!z || z.view !== v) continue;
        let art;
        try { art = await drawable(d.design, g.rgb); } catch (_) { continue; }
        const b = box(z, art, d.design);
        ctx.save();
        ctx.translate(b.cx, b.cy);
        if (z.angle) ctx.rotate((z.angle * Math.PI) / 180);
        ctx.globalAlpha = 0.96;
        ctx.drawImage(art, -b.w / 2, -b.h / 2, b.w, b.h);
        ctx.restore();
        if (opts.outline) {
          ctx.save(); ctx.strokeStyle = 'rgba(200,16,46,.7)'; ctx.setLineDash([g.W / 120, g.W / 200]); ctx.lineWidth = Math.max(2, g.W / 500);
          ctx.strokeRect(b.area.x, b.area.y, b.area.w, b.area.h); ctx.restore();
        }
      }
      ctx.restore();
    }
    return c;
  }
  // Close-up: the print at a size a phone card can show. A square crop around the decorated print
  // areas of one lay-down (the front when it has a print), at least 40% of the garment's height so
  // the garment still reads around a small left-chest print. On a 400 px card a whole-garment
  // mockup left a left-chest or blanket print about 60 px wide (2026-10-11 replays).
  async function closeup(p, colorName, decorations, opts = {}) {
    const g = await garment(p, colorName);
    const decs = decorations || [];
    const on = (v) => decs.map((d) => g.zones.find((z) => z.key === d.location)).filter((z) => z && z.view === v);
    const view = on('front').length ? 'front' : on('back').length ? 'back' : 'front';
    const zs = on(view);
    const full = await mockup(p, colorName, decs, { views: [view] });
    const out = document.createElement('canvas'), N = opts.size || 640;
    out.width = N; out.height = N;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, N, N);
    if (!zs.length) { const k = Math.min(N / full.width, N / full.height); ctx.drawImage(full, (N - full.width * k) / 2, (N - full.height * k) / 2, full.width * k, full.height * k); return out; }
    const x0 = Math.min(...zs.map((z) => z.x)), y0 = Math.min(...zs.map((z) => z.y)), x1 = Math.max(...zs.map((z) => z.x + z.w)), y1 = Math.max(...zs.map((z) => z.y + z.h));
    let side = Math.max(x1 - x0, y1 - y0) * 1.5;
    side = Math.min(Math.max(side, g.H * 0.4), Math.min(g.W, g.H));
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const sx = Math.min(Math.max(cx - side / 2, 0), g.W - side), sy = Math.min(Math.max(cy - side / 2, 0), g.H - side);
    ctx.drawImage(full, sx, sy, side, side, 0, 0, N, N);
    return out;
  }
  // Production file: the location's design alone, transparent, at 150 dpi over the print area. The
  // canvas is the print area (P.area: the zone's print area at its true shape), so the design, which
  // is fitted inside it, is always whole. (It used to be mapped by the zone's width only; on the
  // square blanket zone that ran the bottom of a design off the 20 x 15 canvas, #MV1545.)
  async function artwork(p, colorName, d) {
    const g = await garment(p, colorName);
    const z = g.zones.find((x) => x.key === d.location);
    if (!z) throw new Error(`no zone ${d.location}`);
    let art = await drawable(d.design, g.rgb);
    const b = box(z, art, d.design);
    const c = document.createElement('canvas');
    [c.width, c.height, c.dpi] = P.canvasSize(z, { pod: !!d.design.asIs });
    const k = c.width / b.area.w;
    // Lettering is drawn again at the size it prints, so it is sharp at 150 dpi.
    if (d.design.kind === 'text') { const need = (b.w * k) / art.width; if (need > 1) art = textCanvas(d.design, g.rgb, Math.min(200 * need, 2000)); }
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(art, (b.cx - b.w / 2 - b.area.x) * k, (b.cy - b.h / 2 - b.area.y) * k, b.w * k, b.h * k);
    const id = ctx.getImageData(0, 0, c.width, c.height);
    if (d.design.asIs || d.design.lettering || d.design.layout === 'roster' || d.design.layout === 'pieces') P.solid(id.data); else P.harden(id.data);
    ctx.putImageData(id, 0, 0);
    return c;
  }
  // What a decoration prints as: { inches: [w, h], area: [w, h] (the location's print area), ppi (an
  // upload's real density, else null), capped (the upload is too small to fill the area), partial (share of
  // the print file that is see-through, measured as the POD checker measures it: on the file itself,
  // scaled to at most 2048 px; null for lettering) }. Cached per design, garment and location.
  const infoCache = new Map();
  function printInfo(p, colorName, d) {
    const D = d.design, key = [p.handle, colorName, d.location, D.kind, D.src, D.keepBackground ? 1 : 0, D.text, D.font, D.ink, D.asIs ? 1 : 0, D.widthIn || '', D.pos ? `${D.pos.x},${D.pos.y}` : '', D.arc ? 1 : 0, D.layout || '', D.title || '', D.show || 0].join('|');
    if (infoCache.has(key)) return infoCache.get(key);
    const job = (async () => {
      const g = await garment(p, colorName);
      const z = g.zones.find((x) => x.key === d.location);
      if (!z) return null;
      const art = await drawable(D, g.rgb);
      const b = box(z, art, D);
      let partial = null;
      if (D.kind !== 'text') {
        // The logo-as-is file is measured whole: scaling it down to 2048 px invents see-through edge pixels that the file
        // does not have (it is solid or clear everywhere, P.solid).
        const file = await artwork(p, colorName, d), s = D.asIs ? 1 : Math.min(1, 2048 / Math.max(file.width, file.height));
        const c = document.createElement('canvas'); c.width = Math.round(file.width * s); c.height = Math.round(file.height * s);
        const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(file, 0, 0, c.width, c.height);
        partial = P.partialAlpha(ctx.getImageData(0, 0, c.width, c.height).data);
      }
      return { kind: D.kind, asIs: !!D.asIs, inches: b.inches, area: z.size, ppi: b.ppi, capped: b.capped, sharpIn: b.sharpIn, partial };
    })();
    infoCache.set(key, job);
    job.catch(() => infoCache.delete(key));
    return job;
  }
  // The print file as a PNG tagged with its real density (150 dpi, more for small areas: P.fileDpi). A canvas
  // PNG has no density tag, which reads as 72 dpi, and the POD floor sizes art by the tag.
  async function printBlob(canvas) {
    const blob = await toBlob(canvas, 'image/png');
    return new Blob([P.pngDpi(new Uint8Array(await blob.arrayBuffer()), canvas.dpi || ART_DPI)], { type: 'image/png' });
  }
  // Screen-print ink count of a design on a garment (house inks, shop metafield decorate.inks).
  async function inkCount(d, garmentRgb, inks) {
    if (d.kind === 'text') return 1;
    const C = window.DecorateColors;
    if (!C || !inks || !inks.length) return 3;
    const art = await drawable(d, garmentRgb);
    const c = document.createElement('canvas'), s = Math.min(1, 360 / Math.max(art.width || art.naturalWidth, art.height || art.naturalHeight));
    c.width = Math.max(1, Math.round((art.width || art.naturalWidth) * s)); c.height = Math.max(1, Math.round((art.height || art.naturalHeight) * s));
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(art, 0, 0, c.width, c.height);
    const r = C.detect(ctx.getImageData(0, 0, c.width, c.height).data, rgbHex(garmentRgb), inks);
    return Math.max(1, r.colors.filter((x) => !x.garment).length);
  }
  // A design flattened onto the chroma key its colours are farthest from (for the AI to edit):
  // → { dataUrl, key }. Longest side ≤ 1024 px.
  const KEYS = ['#00ff00', '#ff00ff', '#00ffff', '#0000ff'];
  async function flatOnKey(design, garmentRgb) {
    const art = await drawable(design, garmentRgb);
    const w0 = art.width || art.naturalWidth, h0 = art.height || art.naturalHeight, k = Math.min(1, 900 / Math.max(w0, h0));
    const sm = document.createElement('canvas'); sm.width = 48; sm.height = 48;
    const sctx = sm.getContext('2d', { willReadFrequently: true }); sctx.drawImage(art, 0, 0, 48, 48);
    const d = sctx.getImageData(0, 0, 48, 48).data, cols = [];
    for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 40) cols.push([d[i], d[i + 1], d[i + 2]]);
    let key = KEYS[0], best = -1;
    KEYS.forEach((x) => { const c = hexRgb(x), m = Math.min(...cols.map((y) => Math.hypot(y[0] - c[0], y[1] - c[1], y[2] - c[2])), 999); if (m > best) { best = m; key = x; } });
    // Square canvas with a margin, so the model has room to add a banner or a line of text.
    const side = Math.round(Math.max(w0, h0) * k * 1.15);
    const c = document.createElement('canvas'); c.width = side; c.height = side;
    const ctx = c.getContext('2d'); ctx.fillStyle = key; ctx.fillRect(0, 0, side, side);
    ctx.drawImage(art, (side - w0 * k) / 2, (side - h0 * k) / 2, w0 * k, h0 * k);
    return { dataUrl: c.toDataURL('image/png'), key };
  }
  const toBlob = (canvas, type = 'image/png', q) => new Promise((r) => canvas.toBlob(r, type, q));
  // A PDF logo → page 1 as a PNG, about 3000 px on its long side (a 20 in print at 150 ppi). pdf.js loads only when a
  // shopper picks a PDF. The PDF itself still goes with the order as the shopper's original file.
  const PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/';
  let pdfLib = null;
  function loadPdfJs() {
    if (pdfLib) return pdfLib;
    pdfLib = new Promise((res, rej) => { const s = document.createElement('script'); s.src = `${PDFJS}pdf.min.js`; s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS}pdf.worker.min.js`; res(window.pdfjsLib); }; s.onerror = () => { pdfLib = null; rej(new Error('The PDF reader did not load. Try again, or save your logo as a PNG.')); }; document.head.append(s); });
    return pdfLib;
  }
  async function pdfImage(file) {
    const lib = await loadPdfJs();
    const doc = await lib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const page = await doc.getPage(1), v1 = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: Math.min(12, 3000 / Math.max(v1.width, v1.height)) });
    const c = document.createElement('canvas'); c.width = Math.round(v.width); c.height = Math.round(v.height);
    await page.render({ canvasContext: c.getContext('2d'), viewport: v, background: 'rgba(0,0,0,0)' }).promise;
    return toBlob(trim(c), 'image/png');
  }
  // How well a design reads on a garment: the median WCAG contrast of its opaque pixels against the
  // garment colour (1 = invisible). Black line art on a black hood scored ~1 (2026-10-11); a pale line
  // drawing on a gray hood ~1.6 and was hard to see, so the Studio moves anything under 2.
  async function readability(design, garmentRgb) {
    const a = await drawable(design, garmentRgb);
    const c = document.createElement('canvas'); c.width = 64; c.height = 64;
    const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(a, 0, 0, 64, 64);
    const d = ctx.getImageData(0, 0, 64, 64).data, cs = [];
    for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 128) cs.push(contrast([d[i], d[i + 1], d[i + 2]], garmentRgb));
    if (!cs.length) return 21;
    cs.sort((x, y) => x - y);
    return cs[cs.length >> 1];
  }

  window.StudioRender = { FONTS, INKS, fontsReady, loadImage, garment, zoneKeys, zoneLabel, drawable, flatOnKey, mockup, cleanLogo, placed, trim, pdfImage, closeup, printBox, artwork, printInfo, printBlob, inkCount, inkFor, toBlob, rgbHex, hexRgb, contrast, removeBorder, readability };
})();
